import { Anchor, Breadcrumbs, Card, Grid, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import GuidanceModal from '../../components/UserFlows/guidanceModal';
import ViberMessageForm from '../../components/ViberMessages/ViberMessageForm';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';

const page = `${Page['viberMessage']}`;

const ViberMessagesDetailsPage = () => {
  const navigate = useNavigate();
  const fetch = useFetch();
  const location = useLocation();

  const [viberMessage, setViberMessage] = useState();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [deleteError, setDeleteError] = useState();
  const [guidanceShow, setGuidanceShow] = useState(false);
  const [useInAutomation, setUseInAutomation] = useState(false);

  const { accountId, domainId, viberMessageId } = useParams();

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages/${viberMessageId}`)
      .then(({ viberMessage }) => {
        if (!viberMessage) {
          navigate(`/accounts/${accountId}/domains/${domainId}/viber`);
        }

        setViberMessage(viberMessage);
      })
      .catch(setLoadError);
  }, [fetch, navigate, accountId, domainId, viberMessageId]);

  const update = async (viberMessage, options = {}) => {
    try {
      const { viberMessage: saved } = await fetch(
        `/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages/${viberMessageId}`,
        {
          method: 'PUT',
          body: JSON.stringify(viberMessage),
        }
      );
      if (!options.skipPostSave) {
        setGuidanceShow(false);
        navigate(`/accounts/${accountId}/domains/${domainId}/viber`);
      }
      return saved;
    } catch (e) {
      if (options.skipPostSave) {
        throw e;
      }
      setUserError(e);
      return undefined;
    }
  };

  const del = (cb) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages/${viberMessage.id}`, {
      method: 'DELETE',
    })
      .then(() => {
        if (typeof cb === 'function') {
          cb();
        }
        navigate(location.pathname.replace(`/viber/${viberMessage.id}/details`, '/viber'));
      })
      .catch(setDeleteError);
  };

  const showUserFlow = () => {
    setGuidanceShow(true);
    setUseInAutomation(true);
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (!viberMessage) {
    return <Spinner />;
  }

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics` },
    { title: page, href: `#/accounts/${accountId}/domains/${domainId}/viber` },
    { title: viberMessage.name, href: `#${location.pathname.replace('/details', '/analytics')}` },
    { title: 'Edit' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <>
      <Metadata title={`${page} | ${viberMessage.name} | Edit`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>
      <GuidanceModal
        type="Viber"
        redirect={`/accounts/${accountId}/domains/${domainId}/viber`}
        show={guidanceShow}
        elementTypes={['workflows']}
        element={viberMessage}
        setShow={() => {
          setGuidanceShow(false);
          setUseInAutomation(false);
        }}
        useInAutomation={useInAutomation}
      />
      <Grid>
        <Grid.Col span={12}>
          <Card withBorder>
            <ViberMessageForm
              viberMessage={viberMessage}
              saveViberMessage={(viberMessage, options) => update(viberMessage, options)}
              allowSaveAndSubmit
              deleteViberMessage={(cb) => del(cb)}
              showUserFlow={() => showUserFlow(true)}
              deleteError={deleteError?.message}
              onBack={() => navigate(-1)}
            />
          </Card>
        </Grid.Col>
      </Grid>
      {userError ? (
        <div style={{ textAlign: 'right' }}>
          <ErrorMessage message={userError.message} />
        </div>
      ) : null}
    </>
  );
};

export default ViberMessagesDetailsPage;
